/*
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { __ } from '@web-stories-wp/i18n';
import {
  Button,
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
  Icons,
  Modal,
  PLACEMENT,
} from '@web-stories-wp/design-system';
import { useCallback, useState } from '@web-stories-wp/react';
import { trackEvent } from '@web-stories-wp/tracking';
import styled from 'styled-components';

/**
 * Internal dependencies
 */
import Tooltip from '../../tooltip';
import GridView from './gridView';
import useIsStory from '@src/hooks/useIsStory';

const Box = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

function GridViewButton({ disabled = false }) {
  const isStory = useIsStory();
  if (!isStory) return null;

  const [isGridViewOpen, setIsGridViewOpen] = useState(false);

  const toggleModal = useCallback(() => {
    setIsGridViewOpen((prevIsOpen) => {
      if (disabled && !prevIsOpen) {
        return prevIsOpen;
      }

      const newIsOpen = !prevIsOpen;

      trackEvent('grid_view_toggled', {
        status: newIsOpen ? 'open' : 'closed',
      });

      return newIsOpen;
    });
  }, [disabled, setIsGridViewOpen]);

  return (
    <>
      <Box>
        <Tooltip
          title={disabled ? '' : __('Grid View', 'web-stories')}
          placement={PLACEMENT.TOP}
          hasTail
        >
          <Button
            variant={BUTTON_VARIANTS.SQUARE}
            type={BUTTON_TYPES.TERTIARY}
            size={BUTTON_SIZES.SMALL}
            onClick={toggleModal}
            disabled={disabled}
            aria-label={__('Grid View', 'web-stories')}
          >
            <Icons.Box4 />
          </Button>
        </Tooltip>
      </Box>
      <Modal
        isOpen={isGridViewOpen}
        onClose={toggleModal}
        contentLabel={__('Grid View', 'web-stories')}
        overlayStyles={{
          alignItems: 'stretch',
          backgroundColor: '#131516', // theme.colors.brand.gray[90]
        }}
        contentStyles={{
          pointerEvents: 'none',
          flex: 1,
        }}
      >
        <GridView onClose={toggleModal} />
      </Modal>
    </>
  );
}

GridViewButton.propTypes = {
  disabled: PropTypes.bool,
};

export default GridViewButton;
